/**
 * Browser-side `set_structure_visibility`: view-only, auto-run like a read.
 * Shows or hides structure outlines exactly as the structure list's eye
 * icon does (one visibility event per structure); `hide-all` keeps the
 * selected structure visible, as the list's own hide-all does. Names are
 * resolved with the shared resolver: an unknown or ambiguous name is
 * reported, never guessed. Ports are injected (React-free).
 */
import { resolveRoi } from '../core/roiResolver';
import { asRecord } from '../core';
import type {
    ListedStructure,
    SetStructureVisibilityOutput,
} from '../../contract';

export interface VisibilityStructure {
    readonly roiNumber: number;
    readonly name: string;
    readonly type: string;
}

export interface VisibilityDeps {
    readonly structures: ReadonlyArray<VisibilityStructure>;
    /** The structure selected in the list, or undefined. */
    readonly selectedRoiNumber: number | undefined;
    /** The eye icon's event for one structure. */
    setVisible(roiNumber: number, visible: boolean): void;
}

type Action = 'show' | 'hide' | 'show-only' | 'show-all' | 'hide-all';
const ACTIONS: ReadonlySet<string> = new Set<Action>([
    'show',
    'hide',
    'show-only',
    'show-all',
    'hide-all',
]);

const ref = (s: VisibilityStructure) => ({
    roiNumber: s.roiNumber,
    name: s.name,
});

export function setStructureVisibility(
    input: unknown,
    deps: VisibilityDeps
): SetStructureVisibilityOutput {
    const o = asRecord(input);
    const action = o?.action;
    if (typeof action !== 'string' || !ACTIONS.has(action)) {
        return { status: 'unavailable', reason: 'invalid visibility action' };
    }
    if (deps.structures.length === 0) {
        return { status: 'unavailable', reason: 'no structures are loaded' };
    }

    const all = deps.structures;
    let show: VisibilityStructure[] = [];
    let hide: VisibilityStructure[] = [];
    const notFound: string[] = [];

    if (action === 'show-all') {
        show = [...all];
    } else if (action === 'hide-all') {
        // Like the list's hide-all: the selected structure stays visible.
        hide = all.filter((s) => s.roiNumber !== deps.selectedRoiNumber);
    } else {
        const names = Array.isArray(o?.roiNames) ? o.roiNames : [];
        if (names.length === 0 || !names.every((n) => typeof n === 'string')) {
            return {
                status: 'unavailable',
                reason: `${action} needs the structure names`,
            };
        }
        const listed: ListedStructure[] = all.map((s) => ({
            roiNumber: s.roiNumber,
            name: s.name,
            volumeCm3: null,
            type: s.type,
        }));
        const named: VisibilityStructure[] = [];
        for (const name of names as string[]) {
            const r = resolveRoi(name, listed);
            if (r.kind !== 'resolved') {
                notFound.push(name);
                continue;
            }
            const s = all.find((x) => x.roiNumber === r.structure.roiNumber);
            if (s && !named.includes(s)) {
                named.push(s);
            }
        }
        if (action === 'show') {
            show = named;
        } else if (action === 'hide') {
            hide = named;
        } else {
            show = named;
            hide = all.filter((s) => !named.includes(s));
        }
    }

    show.forEach((s) => deps.setVisible(s.roiNumber, true));
    hide.forEach((s) => deps.setVisible(s.roiNumber, false));
    return {
        status: 'ok',
        shown: show.map(ref),
        hidden: hide.map(ref),
        notFound,
    };
}
